import moment from 'moment';
import React from 'react';
import { TFunction } from 'i18next';
import Money from 'src/common/entity/basic-types/Money';
import { StoreModuleType } from 'src/components/administration/operator-store/types';
import { dateFormatter, priceFormatter } from 'src/utils/formatters';
import { SanitizedInnerHTML, TOMCAT_URL } from 'src/common';
import BigNumber from 'bignumber.js';
import { blue700 } from 'material-ui/styles/colors';
import DownloadIcon from 'material-ui/svg-icons/file/file-download';

interface ModuleLongDescriptionProps {
    type: StoreModuleType;
    fee: Money;
    oneTimeFee: boolean;
    deactivateModuleAt: string;
    oneTimeFeeBilledOnce: boolean;
    isChargedThisMonth: boolean;
    transactionFee?: Money;
    variableFee?: Money;
    hasMinimumFee?: Boolean;
    trialIsActive: boolean;
    moduleActiveUntilEndOfMonth: boolean;
    active: Boolean;
    isWebsiteIntegrationModule: boolean;
    t: TFunction;
    module: Module;
    oneTimeFeeAmount?: Money;
}

interface InfoDocument {
    directory: 'PUBLIC' | 'PRIVATE';
    filename: string;
    path: string;
    translation: string | null;
}

interface Module {
    id: number;
    type: string;
    active: boolean;
    deactivateModuleAt: string | null;
    featureInStore: boolean | null;
    fee: Money;
    variableFee: Money | null;
    transactionFee: Money | null;
    hasMinimumFee: boolean;
    oneTimeFee: boolean;
    oneTimeFeeBilledOnce: boolean;
    oneTimeFeeModuleRemainsActive: boolean;
    isChargedThisMonth: boolean;
    moduleActiveUntilEndOfMonth: boolean;
    trialIsActive: boolean;
    infoDocuments?: InfoDocument[];
    oneTimeFeeAmount?: Money;
}

export const ModuleLongDescription = ({
    type,
    fee,
    oneTimeFee,
    transactionFee,
    trialIsActive,
    t,
    isChargedThisMonth,
    moduleActiveUntilEndOfMonth,
    oneTimeFeeBilledOnce,
    deactivateModuleAt,
    variableFee,
    hasMinimumFee,
    isWebsiteIntegrationModule,
    active,
    module,
    oneTimeFeeAmount,
}: ModuleLongDescriptionProps) => {
    const nextBillingDate = moment().add(1, 'month').startOf('month');
    const trialActiveUntil = moment(deactivateModuleAt);
    const isFreeModule =
        fee.amount != null && Number(fee.amount) === 0 && variableFee == null && oneTimeFeeAmount == null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            <Section>
                <h4>{t('common_phrases.description')}</h4>
                <p>
                    <SanitizedInnerHTML html={t(`modules_store.types.${type}.long_description`)} />
                </p>
            </Section>

            {module.infoDocuments && module.infoDocuments.length > 0 && (
                <Section>
                    <h4>{t('modules_store.guides')}</h4>
                    {module.infoDocuments.map((document, index) => (
                        <div key={index}>
                            <a
                                href={`${TOMCAT_URL}public/documents/${document.filename}`}
                                style={{ display: 'flex', alignItems: 'center' }}
                                target="_blank"
                                rel="noreferrer">
                                <DownloadIcon color={blue700} fontSize={30} />
                                <span>
                                    {t(`modules_store.types.${type}.title`).toLowerCase().replace(/ /g, '-')}-
                                    {index + 1}
                                </span>
                            </a>
                        </div>
                    ))}
                </Section>
            )}

            <Section>
                <h4>{t('modules_store.payment_details')}</h4>
                <div className="col-md-5">
                    <table className="table table-responsive small" style={{ border: 'none', textAlign: 'left' }}>
                        <tbody>
                            {fee.amount != null && Number(fee.amount) === 0 && variableFee == null && (
                                <tr style={{ border: 'none', padding: 2 }}>
                                    <td style={{ border: 'none', padding: 2 }}>
                                        <span className="text-success bold" style={{ fontWeight: 'bold' }}>
                                            {t('modules_store.free')}
                                        </span>
                                    </td>
                                </tr>
                            )}
                            {(hasMinimumFee || fee.amount > new BigNumber(0)) && (
                                <tr style={{ border: 'none', padding: 2 }}>
                                    <td style={{ border: 'none', padding: 2 }}>
                                        {hasMinimumFee
                                            ? t('modules_store.min_monthly_fee')
                                            : t('modules_store.monthly_fee')}
                                    </td>
                                    <td style={{ border: 'none', padding: 2, textAlign: 'right' }}>
                                        {priceFormatter(fee)}
                                    </td>
                                </tr>
                            )}
                            {oneTimeFeeAmount && !isFreeModule && (
                                <tr style={{ border: 'none', padding: 2 }}>
                                    <td style={{ border: 'none', padding: 2 }}>
                                        {t('modules_store.one_time_fee', { fee: '' })}
                                    </td>

                                    <td style={{ border: 'none', padding: 2, textAlign: 'right' }}>
                                        {priceFormatter(oneTimeFeeAmount)}
                                    </td>
                                </tr>
                            )}
                            {variableFee && (
                                <tr style={{ border: 'none', padding: 2 }}>
                                    <td style={{ border: 'none', padding: 2 }}>
                                        {t('modules_store.monthly_variable_fee')}
                                    </td>
                                    <td style={{ border: 'none', padding: 2, textAlign: 'right' }}>
                                        {priceFormatter(variableFee)}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {(isChargedThisMonth || !oneTimeFee) && !trialIsActive && (
                        <p>
                            {t('modules_store.next_billing_date', {
                                date: dateFormatter(nextBillingDate),
                            })}
                        </p>
                    )}
                </div>

                <div className="col-md-12">
                    {trialIsActive && (
                        <p>{t('modules_store.trial_active_long', { date: trialActiveUntil.format('DD.MM.YYYY') })}</p>
                    )}
                    {moduleActiveUntilEndOfMonth && (
                        <p>{t('modules_store.active_until_eom', { date: trialActiveUntil.format('DD.MM.YYYY') })}</p>
                    )}
                </div>
                {isWebsiteIntegrationModule && active && (
                    <div className="col-md-12">
                        <p>
                            <SanitizedInnerHTML
                                html={t('modules_store.info_website_integration_module', {
                                    mail: 'partner@busfinder.com',
                                })}
                            />
                        </p>
                    </div>
                )}
            </Section>
        </div>
    );
};

const Section: React.FC = ({ children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>{children}</div>
);
